import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormField } from '../../../model/responsible/FormField';
import { RadioGroupOption } from '../../../model/RadioGroupOption';
import { Exclusion } from '../../../model/responsible/Exclusion';
import { ExclusionAPI } from '../../../services/responsible/exclusion.service';
import { CodedResponseModel } from '../../../model/CodedResponseModel';

@Component({
    selector: 'app-multi-radio-group-editor',
    templateUrl: './multi-radio-group-editor.component.html',
    styleUrls: ['./multi-radio-group-editor.component.scss'],
})
export class MultiRadioGroupEditorComponent implements OnInit {
    @Input() public fieldData!: FormField;
    @Input() public fields: FormField[] = [];

    @Output() public changesMade = new EventEmitter<void>();

    public options: RadioGroupOption[] = [];
    public label: string = '';
    public exclusions: Exclusion[] = [];
    public loadingExclusions: boolean = true;

    constructor(private exclusionApi: ExclusionAPI) {}

    public ngOnInit() {
        if (this.fields.length <= 1) {
            this.fields = [new FormField()];
            this.fields[0].type = 'multi-radio-group';
        }
        if (this.fieldData.filetypes) {
            // FormField::filetypes is used as an array of options in JSON format.
            const data = JSON.parse(this.fieldData.filetypes);

            this.options = data.options;
            this.fields = data.fields;
            this.label = data.label || '';
        } else {
            this.options = [{ label: '', value: '0' }];
        }
        // Fetch exclusions for the customs dropdown
        this.exclusionApi.index().subscribe((res: any) => {
            let response: any[] = [];
            if (res && typeof res === 'object' && 'data' in res) {
                const codedResponse = CodedResponseModel.create(res);
                const decodedData = codedResponse.decodeData();
                if (Array.isArray(decodedData)) {
                    response = decodedData;
                } else {
                    response = [];
                }
            } else if (Array.isArray(res)) {
                response = res;
            } else {
                response = [];
            }
            this.exclusions = response.map((e: any) => Exclusion.create(e));
            this.loadingExclusions = false;
        });
    }

    public updateData() {
        this.fieldData.filetypes = JSON.stringify({
            options: this.options,
            fields: this.fields,
            label: this.label,
        });
        this.changesMade.emit();
    }

    public removeOption(index: number) {
        this.options.splice(index, 1);
        this.updateData();
    }

    public insertOption() {
        this.options.push({ label: '', value: this.options.length.toString() });
    }

    public insertOptionAt(index: number) {
        this.options.splice(index + 1, 0, { label: '', value: this.options.length.toString() });
        this.updateData();
    }

    public moveOptionUp(index: number) {
        if (index <= 0) return;
        const item = this.options.splice(index, 1)[0];
        this.options.splice(index - 1, 0, item);
        this.updateData();
    }

    public moveOptionDown(index: number) {
        if (index >= this.options.length - 1) return;
        const item = this.options.splice(index, 1)[0];
        this.options.splice(index + 1, 0, item);
        this.updateData();
    }

    public canMoveUp(index: number): boolean {
        return index > 0;
    }

    public canMoveDown(index: number): boolean {
        return index < this.options.length - 1;
    }

    public trackByFn(index: number, item: any) {
        return index;
    }

    public insertFieldAt(index: number) {
        const newField = new FormField();
        newField.type = 'multi-radio-group';
        newField.id = this.fields.length;
        this.fields.splice(index + 1, 0, newField);
        this.updateData();
    }

    public moveFieldUp(index: number) {
        if (index <= 0) return;
        const item = this.fields.splice(index, 1)[0];
        this.fields.splice(index - 1, 0, item);
        this.updateData();
    }

    public moveFieldDown(index: number) {
        if (index >= this.fields.length - 1) return;
        const item = this.fields.splice(index, 1)[0];
        this.fields.splice(index + 1, 0, item);
        this.updateData();
    }

    public canMoveFieldUp(index: number): boolean {
        return index > 0;
    }

    public canMoveFieldDown(index: number): boolean {
        return index < this.fields.length - 1;
    }

    public insertField() {
        this.insertFieldAt(this.fields.length - 1);
    }

    public removeField(index: number) {
        if (this.fields.length <= 1) return;
        this.fields.splice(index, 1);
        this.updateData();
    }
}
